import { useApolloClient } from '@apollo/client';
import { Button, Divider } from '@busybusy/webapp-react-ui';
import { Member } from '__generated__/graphql';
import { MEMBER_NAME_QUERY } from 'apollo/queries/member-queries';
import classNames from 'classnames';
import DetailHeaderWithText from 'components/foundation/DetailHeaderWithText/DetailHeaderWithText';
import { useCertificationChecker } from 'containers/certification-status-report/hooks/useCertificationChecker';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ClassName } from 'types/ClassName';
import HeaderDialog from '../HeaderDialog/HeaderDialog';

export interface IMissingCertificationsDialogProps {
  className?: ClassName;
  isOpen: boolean;
  memberIds: string[];
  equipmentIds: string[];
  onClose: () => void;
  onSubmit?: () => void;
}

export interface IMissingCertificationsObject {
  memberName: string;
  missingCerts: string[];
}

const MissingCertificationsDialog = (props: IMissingCertificationsDialogProps) => {
  const { className, isOpen, memberIds, equipmentIds, onClose, onSubmit } = props;

  const { getMissingCertifications } = useCertificationChecker();
  const client = useApolloClient();

  const [missingCertsDetails, setMissingCertsDetails] = useState<IMissingCertificationsObject[]>([]);

  useEffect(() => {
    const fetchMissingCerts = async () => {
      if (isOpen && memberIds.length > 0 && equipmentIds.length > 0) {
        const memberResults = await client.query<{ members: Member[] }>({
          query: MEMBER_NAME_QUERY,
          variables: { filter: { id: { contains: memberIds } } },
          fetchPolicy: 'network-only',
        });
        const members = memberResults.data.members;
        const results = await getMissingCertifications(memberIds, equipmentIds, members);
        setMissingCertsDetails(results);
      } else {
        setMissingCertsDetails([]);
      }
    };

    fetchMissingCerts();
  }, [isOpen, memberIds, equipmentIds, getMissingCertifications]);

  const classes = classNames('missing-certifications-dialog', className);

  return (
    <HeaderDialog
      title={t('Missing Certifications')}
      className={classes}
      isOpen={isOpen}
      onClose={onClose}
      divider={false}
    >
      <div className="mx-8 my-4">
        {t(
          'One or more employees are missing the following certifications required to operate the selected equipment. '
        )}

        {onSubmit && <div className="mt-4">{t('Are you sure you want to continue?')}</div>}

        {missingCertsDetails.length > 0 && (
          <>
            {missingCertsDetails.map(({ memberName, missingCerts }, index) => (
              <>
                {missingCerts.length > 0 && (
                  <DetailHeaderWithText
                    key={index}
                    title={memberName}
                    className="my-2"
                    value={
                      <ul>
                        {missingCerts.map((cert, index) => (
                          <li key={index}>{cert}</li>
                        ))}
                      </ul>
                    }
                  />
                )}
              </>
            ))}
          </>
        )}
      </div>
      <Divider />
      <div className="mx-8 my-4">
        {onSubmit && (
          <Button type="danger" onClick={onSubmit} className="mr-2">
            {t('Continue')}
          </Button>
        )}
        <Button type="secondary" onClick={onClose}>
          {t('Close')}
        </Button>
      </div>
    </HeaderDialog>
  );
};

export default MissingCertificationsDialog;
